import classNames from 'classnames'
import React, { PropsWithChildren } from 'react'
import { useTranslation } from 'react-i18next'

import { useNavigate } from '@lib/react-router-dom'

import { useSessionEntity } from '@context/EntityContext'
import { useProductReadContext } from '@context/ProductContext'

import { CarouselDots, CarouselHandlersProps, CarouselNumberDots } from '@ui/Carousel/Carousel'
import { Body1, Body2, Heading4, Heading6 } from '@ui/Typography'
import { ActionIcon } from '@ui/components/ActionIcon'
import { Button, IconButton } from '@ui/components/Button'
import { ArrowIcon } from '@ui/icons/Arrow.icon'
import { ChevronIcon } from '@ui/icons/Chevron.icon'
import { CloseIcon } from '@ui/icons/Close.icon'
import { DummyIcon } from '@ui/icons/Dummy.icon'

import { useAccountActionAccessCheck } from '@utils/AccountAccess/accountAccessCheck'
import { TickmillCompaniesEnum } from '@utils/companyName.utils'
import { useWindowResize } from '@utils/domUtils'
import { CommonPaths } from '@utils/path'
import { isOne } from '@utils/validations'

import { TickmillProductType } from '@model/TickmillProductType'

import { AppMarketLink } from '@global/appMarketLink/mobile/AppMarketLink'

import { AccessYourTradingPlatformGraphic } from '@assets/graphics/AccessYourTradingPlatform.graphic'
import { CreateTradingAccountGraphic } from '@assets/graphics/CreateTradingAccount.graphic'
import { DiscoverTradeIdeasGraphic } from '@assets/graphics/DiscoverTradeIdeas.graphic'
import { FundWalletGraphic } from '@assets/graphics/FundWallet.graphic'
import { GettingStartedCongratulationsGraphic } from '@assets/graphics/GettingStartedCongratulations.graphic'
import { GettingStartedDepositGraphic } from '@assets/graphics/GettingStartedDeposit.graphic'
import { GettingStartedTransferFundsIcon } from '@assets/graphics/GettingStartedDepositFunds.graphic'
import { GettingStartedEmailGraphic } from '@assets/graphics/GettingStartedEmail.graphic'
import { GettingStartedMobileDownloadGraphic } from '@assets/graphics/GettingStartedMobileDownload.graphic'
import { GettingStartedSubscribeMarketDataGraphic } from '@assets/graphics/GettingStartedSubscribeMarketData.graphic'
import { GettingStartedSubscribePlatformGraphic } from '@assets/graphics/GettingStartedSubscribePlatform.graphic'
import { MakeDepositGraphic } from '@assets/graphics/MakeDeposit.graphic'
import { SecureClientAreaGraphic } from '@assets/graphics/SecureClientArea.graphic'

import styles from './FirstTimeGuide.module.scss'

export const EnableTwoFactorAuthCarouselChild: React.FC<Partial<CarouselHandlersProps>> = (
  props
) => {
  const { t } = useTranslation()
  const isMobile = useWindowResize()
  const navigate = useNavigate()

  const handleButton = () => {
    navigate('/profile/security-settings')
  }

  return (
    <GuideCarouselChildCFD
      {...props}
      title={t('Secure Your Client Area')}
      description={t('Enable Two-Factor Authentication (2FA)')}
      icon={<SecureClientAreaGraphic />}
      button={
        <Button.Primary size='lg' onClick={handleButton} fullWidth={isMobile}>
          {t('TwoFactorAuth.Enable 2FA')}
        </Button.Primary>
      }
    />
  )
}

export const FundWalletGuideCarouselChild: React.FC<
  Partial<CarouselHandlersProps> & { onDeposit: () => void; isWalletsLoading: boolean }
> = (props) => {
  const { t } = useTranslation()
  const isMobile = useWindowResize()

  const { onDeposit, disabledAction, isWalletsLoading } = props

  return (
    <GuideCarouselChildCFD
      {...props}
      title={t('Fund your Wallet')}
      description={t(
        'Wallet.Choose your preferred payment method and add funds to your Tickmill Wallet to get started with live trading.'
      )}
      icon={<FundWalletGraphic />}
      button={
        <Button.Primary
          size='lg'
          loading={isWalletsLoading}
          disabled={disabledAction}
          fullWidth={isMobile}
          onClick={onDeposit}
        >
          {t('Deposit')}
        </Button.Primary>
      }
    />
  )
}

export const TransferWalletToTradingAccountGuideCarouselChild: React.FC<
  Partial<CarouselHandlersProps>
> = (props) => {
  const { t } = useTranslation()
  const isMobile = useWindowResize()
  const navigate = useNavigate()
  const { checkAccountOpenAccountAccess } = useAccountActionAccessCheck()

  const handleButton = () => {
    checkAccountOpenAccountAccess(() => navigate(CommonPaths.NewLiveTradingAccount))
  }

  return (
    <GuideCarouselChildCFD
      {...props}
      title={t('Trading Account.Create Your Trading Account')}
      description={t(
        'Trading Account.Pick your trading platform, then choose your base currency and account type to open your first live trading account.'
      )}
      icon={<CreateTradingAccountGraphic />}
      button={
        <Button.Primary size='lg' fullWidth={isMobile} onClick={handleButton}>
          {t('Create Trading Account')}
        </Button.Primary>
      }
    />
  )
}

export const TransferToTradingAccountCarouselChild: React.FC<Partial<CarouselHandlersProps>> = (
  props
) => {
  const { t } = useTranslation()
  const isMobile = useWindowResize()
  const navigate = useNavigate()
  const { checkAccountOpenAccountAccess } = useAccountActionAccessCheck()

  const handleButton = () => {
    checkAccountOpenAccountAccess(() => navigate(`${CommonPaths.TradersRoomWallets}/transfer`))
  }

  return (
    <GuideCarouselChildCFD
      {...props}
      title={t('Transfer Funds')}
      description={t(
        "Add money from your Tickmill Wallet into your Trading Account so you're ready to place your first trade."
      )}
      icon={<MakeDepositGraphic />}
      button={
        <Button.Primary size='lg' fullWidth={isMobile} onClick={handleButton}>
          {t('Transfer Funds')}
        </Button.Primary>
      }
    />
  )
}

export const DownloadTradingCarouselChild: React.FC<Partial<CarouselHandlersProps>> = (props) => {
  const { t } = useTranslation()
  const isMobile = useWindowResize()
  const navigate = useNavigate()

  const handleButton = () => {
    navigate('/dashboard/traders-room/platforms/mt5')
  }

  return (
    <GuideCarouselChildCFD
      {...props}
      title={t('Trading Account.Access your Trading Platform')}
      description={t(
        'Trading Account.Download the platform you selected: TradingView, MetaTrader 4/5, or Tickmill Trader, and start trading on your device.'
      )}
      icon={<AccessYourTradingPlatformGraphic />}
      button={
        <Button.Primary size='lg' fullWidth={isMobile} onClick={handleButton}>
          {t('Trading Account.Explore Platforms')}
        </Button.Primary>
      }
    />
  )
}

export const TickmillMobileAppCarouselChild: React.FC<Partial<CarouselHandlersProps>> = (props) => {
  const { t } = useTranslation()

  const isLastSlide = props?.active === 5

  return (
    <GuideCarouselChildCFD
      {...props}
      title={t('Tickmill Mobile app')}
      description={t(
        'Trading Account.Manage your trading accounts on the go. Use the app to deposit, withdraw, and monitor your balance and equity anytime.'
      )}
      icon={<GettingStartedMobileDownloadGraphic />}
      content={
        isLastSlide && (
          <div className={styles.appLinkContainer}>
            <AppMarketLink wrapperClassName={styles.appLinkWrapper} justIcons hideCFDMessage />
          </div>
        )
      }
    />
  )
}

export const DiscoverTradeIdeasCarouselChild: React.FC<Partial<CarouselHandlersProps>> = (
  props
) => {
  const { t } = useTranslation()
  const isMobile = useWindowResize()
  const navigate = useNavigate()

  const handleButton = () => {
    navigate('/dashboard/tools/research-terminal')
  }
  return (
    <GuideCarouselChildCFD
      {...props}
      title={t('Trading Account.Discover Trade Ideas')}
      description={t(
        'Use our Research Terminal to explore trade ideas, news, and sentiment: all in one place, in real time.'
      )}
      icon={<DiscoverTradeIdeasGraphic />}
      button={
        <Button.Primary size='lg' fullWidth={isMobile} onClick={handleButton}>
          {t('Trading Account.Discover Ideas')}
        </Button.Primary>
      }
    />
  )
}

export const FundYourWalletCarouselChild: React.FC<
  Partial<CarouselHandlersProps> & { onDeposit: () => void }
> = (props) => {
  const { t } = useTranslation()
  const { onDeposit } = props

  return (
    <GuideCarouselChild
      {...props}
      title={t('Fund your Tickmill Wallet')}
      description={t('Min. 1,000 USD or equivalent in GBP, EUR, HKD, SGD and CHF')}
      icon={<GettingStartedDepositGraphic />}
      button={
        <Button.Primary size='lg' onClick={onDeposit}>
          {t('Learning.Start Deposit')}
        </Button.Primary>
      }
    />
  )
}

export const TransferFundsFromWalletToTradingAccountCarouselChild: React.FC<
  Partial<CarouselHandlersProps>
> = (props) => {
  const { t } = useTranslation()

  const navigate = useNavigate()

  const handleButton = () => {
    navigate(`${CommonPaths.TradersRoomWallets}/transfer`)
  }

  return (
    <GuideCarouselChild
      {...props}
      title={t('Learning.Transfer funds from Wallet to Trading Account')}
      description={t(
        'Learning.Please note that the payment for the required trading platform and market'
      )}
      icon={<GettingStartedTransferFundsIcon />}
      button={
        <Button.Primary size='lg' onClick={handleButton}>
          {t('Learning.Start Transfer')}
        </Button.Primary>
      }
    />
  )
}

export const SubscribeToTradingPlatformCarouselChild: React.FC<Partial<CarouselHandlersProps>> = (
  props
) => {
  const { t } = useTranslation()

  const navigate = useNavigate()

  const handleButton = () => {
    navigate('/dashboard/subscriptions/trading-platforms')
  }

  return (
    <GuideCarouselChild
      {...props}
      title={t('Learning.Subscribe to Trading Platform')}
      icon={<GettingStartedSubscribePlatformGraphic />}
      button={
        <Button.Primary size='lg' onClick={handleButton}>
          {t('Learning.Subscribe')}
        </Button.Primary>
      }
    />
  )
}

export const SubscribeToMarketDataCarouselChild: React.FC<Partial<CarouselHandlersProps>> = (
  props
) => {
  const { t } = useTranslation()

  const navigate = useNavigate()

  const handleButton = () => {
    navigate('/dashboard/subscriptions/market-data')
  }

  return (
    <GuideCarouselChild
      {...props}
      title={t('Learning.Subscribe to Market Data')}
      icon={<GettingStartedSubscribeMarketDataGraphic />}
      button={
        <Button.Primary size='lg' onClick={handleButton}>
          {t('Learning.Subscribe')}
        </Button.Primary>
      }
    />
  )
}

export const CheckEmailInstructionsCarouselChild: React.FC<Partial<CarouselHandlersProps>> = (
  props
) => {
  const { t } = useTranslation()

  return (
    <GuideCarouselChild
      {...props}
      title={t(
        'Learning.Check your email for instructions on how to connect to a third party platform'
      )}
      icon={<GettingStartedEmailGraphic />}
    />
  )
}

export const CongratulationsCarouselChild: React.FC<Partial<CarouselHandlersProps>> = (props) => {
  const { t } = useTranslation()

  return (
    <GuideCarouselChild
      {...props}
      title={t('Learning.Congratulations!')}
      description={t('Learning.All Required steps done and you can start using ETD products')}
      icon={<GettingStartedCongratulationsGraphic />}
    />
  )
}

const AdditionalInfo = () => {
  const { t } = useTranslation()
  return (
    <div className='is-flex is-justify-content-center is-flex-direction-column mb-5'>
      <p>
        <Body1 medium>{t('Tickmill Trader:')}</Body1>{' '}
        <Body1>{t('User-friendly Mobile App for iOS and Android')}</Body1>
      </p>
      <p>
        <Body1 medium>{t('MT4MT5')}</Body1> <Body1>{t('Desktop and Mobile App')}</Body1>
      </p>
    </div>
  )
}

interface GuideStepProps extends Partial<CarouselHandlersProps> {
  title?: string
  description?: string
  icon?: React.ReactNode
  button?: React.ReactNode
  content?: React.ReactNode
  className?: string
  onConfirmationModalOpen?(): void
  hasAdditionalInfo?: boolean
}

const GuideCarouselChild: React.FC<PropsWithChildren<GuideStepProps>> = (props) => {
  const {
    title,
    description,
    icon,
    button,
    onConfirmationModalOpen,
    active,
    items,
    isFirstSlide,
    isLastSlide,
    onScrollNext,
    onScrollPrev,
    onScrollActive,
    children,
    className,
    hasAdditionalInfo,
  } = props

  const isMobile = useWindowResize()
  const entity = useSessionEntity()

  const leftArrowHidden = isFirstSlide || isMobile ? 'is-invisible' : ''
  const rightArrowHidden = isLastSlide || isMobile ? 'is-invisible' : ''
  const carouselSlide = props.active

  const isSeychellesOrAsiaEntity = () =>
    entity === TickmillCompaniesEnum.TICKMILL_SC || entity === TickmillCompaniesEnum.TICKMILL_AS

  const { product } = useProductReadContext()

  return (
    <div
      className={classNames(styles.card, {
        [styles.slideCFD]: carouselSlide !== 4 && product === TickmillProductType.CFD,
        [styles.fourthSlideCFD]:
          carouselSlide === 3 && product === TickmillProductType.CFD && isSeychellesOrAsiaEntity(),
        [styles.slideETD]: !isOne(carouselSlide) && product === TickmillProductType.ETD,
        [styles.secondSlideETD]: isOne(carouselSlide) && product === TickmillProductType.ETD,
        [styles.fifthSlideCFD]: carouselSlide === 4 && product === TickmillProductType.CFD,
      })}
    >
      <div className='columns is-8 is-flex-grow-1'>
        <div className={classNames(' is-flex-grow-1 column text-align-center', styles.cardWrapper)}>
          <div className={styles.infoBlock}>
            <div className={styles.close}>
              <IconButton.Ghost icon={CloseIcon} onClick={onConfirmationModalOpen} />
            </div>
            <IconButton.Ghost
              icon={leftArrowHidden ? DummyIcon : ArrowIcon.Left}
              onClick={onScrollPrev}
            />
            <div className={className || styles.infoContent}>
              <div className={styles.info}>
                <Heading6 medium>{title}</Heading6>
                {description && <Body2 className='mb-5 text-secondary'>{description}</Body2>}
                {hasAdditionalInfo && <AdditionalInfo />}
                {button && <div className='mb-5'>{button}</div>}
                <div>
                  <CarouselDots slides={items} active={active} onActive={onScrollActive} />
                </div>
              </div>
              {children && <div className='mt-4'>{children}</div>}
            </div>
            <IconButton.Ghost
              icon={rightArrowHidden ? DummyIcon : ArrowIcon.Right}
              onClick={onScrollNext}
            />
          </div>
        </div>
        {icon && (
          <div className='is-flex-grow-1 column'>
            <div className={styles.icon}>{icon}</div>
          </div>
        )}
      </div>
    </div>
  )
}

const GuideCarouselChildCFD: React.FC<PropsWithChildren<GuideStepProps>> = (props) => {
  const {
    title,
    description,
    icon,
    button,
    onConfirmationModalOpen,
    active = 0,
    items,
    onScrollActive,
    onScrollNext,
    onScrollPrev,
    className,
    hasAdditionalInfo,
    isFirstSlide,
    isLastSlide,
    content,
  } = props

  const leftArrowHidden = isFirstSlide ? 'is-invisible' : ''
  const rightArrowHidden = isLastSlide ? 'is-invisible' : ''

  return (
    <div className={classNames(styles.carouselWrapper)}>
      <>
        <div className={styles.carouselHeaderMobile}>
          <div className={styles.carouselPagination}>
            <div className={styles.leftArrow}>
              <ActionIcon
                icon={{
                  icon: ChevronIcon.Left,
                  color: leftArrowHidden ? 'text-base-em' : 'text-high-em',
                }}
                size='x24'
                onClick={() => !leftArrowHidden && onScrollPrev?.()}
              />
            </div>
            <p>
              {active + 1} <span className={styles.active}>/ {items?.length}</span>
            </p>
            <div className={styles.rightArrow}>
              <ActionIcon
                icon={{
                  icon: ChevronIcon.Right,
                  color: rightArrowHidden ? 'text-base-em' : 'text-high-em',
                }}
                size='x24'
                onClick={() => !rightArrowHidden && onScrollNext?.()}
              />
            </div>
          </div>
          <div className={styles.close}>
            <IconButton.Ghost icon={CloseIcon} onClick={onConfirmationModalOpen} />
          </div>
        </div>
        <div className={classNames(styles.cardWrapper)}>
          <div className={styles.infoBlock}>
            <div className={className || styles.infoContent}>
              <div className={styles.info}>
                <div>
                  <Heading4 medium>{title}</Heading4>
                  {description && (
                    <div className={styles.description}>
                      <Body1>{description}</Body1>
                    </div>
                  )}
                  {hasAdditionalInfo && <AdditionalInfo />}
                  <div className={styles.button}>{button}</div>
                </div>

                <div className={styles.carouselHeaderDesktop}>
                  <div className={styles.carouselPagination}>
                    <div className={styles.leftArrow}>
                      <ActionIcon
                        icon={{
                          icon: ChevronIcon.Left,
                          color: leftArrowHidden ? 'text-base-em' : 'text-high-em',
                        }}
                        size='x24'
                        onClick={() => !leftArrowHidden && onScrollPrev?.()}
                      />
                    </div>
                    <CarouselNumberDots slides={items} active={active} onActive={onScrollActive} />
                    <div className={styles.rightArrow}>
                      <ActionIcon
                        icon={{
                          icon: ChevronIcon.Right,
                          color: rightArrowHidden ? 'text-base-em' : 'text-high-em',
                        }}
                        size='x24'
                        onClick={() => !rightArrowHidden && onScrollNext?.()}
                      />
                    </div>
                  </div>
                </div>

                {content && <>{content}</>}
              </div>
            </div>
          </div>
        </div>
        {icon && (
          <>
            <div className={styles.close}>
              <IconButton.Ghost icon={CloseIcon} onClick={onConfirmationModalOpen} />
            </div>
            <div className={styles.icon}>{icon}</div>
          </>
        )}
      </>
    </div>
  )
}
